import React, { useState, useEffect } from "react";
import { Eye, EyeOff, Check, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PASSWORD_RULES, passwordMeetsPolicy, passwordStrength } from "@/lib/password";

const STRENGTH_BAR = { 1: "bg-red-500", 2: "bg-amber-500", 3: "bg-emerald-500", 4: "bg-green-600" };
const STRENGTH_TEXT = { 1: "text-red-500", 2: "text-amber-600", 3: "text-emerald-600", 4: "text-green-600" };

/**
 * New-password + confirm-password fields with show/hide toggles, a live
 * requirements checklist, and an immediate green/red match indicator.
 *
 * Parent owns the password value; the confirm value is internal. `onValidChange`
 * fires with `true` only when every rule passes AND the two fields match.
 */
export function PasswordFields({
  password,
  setPassword,
  onValidChange,
  idPrefix = "password",
  label = "Password",
  confirmLabel = "Confirm password",
  autoComplete = "new-password",
}) {
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const pw = password || "";
  const rules = PASSWORD_RULES.map((r) => ({ ...r, ok: r.test(pw) }));
  const rulesOk = passwordMeetsPolicy(pw);
  const strength = passwordStrength(pw);
  const hasConfirm = confirm.length > 0;
  const match = hasConfirm && pw === confirm;
  const valid = rulesOk && match;

  useEffect(() => {
    onValidChange?.(valid);
  }, [valid, onValidChange]);

  const ToggleBtn = ({ shown, onClick, testId }) => (
    <button
      type="button"
      onClick={onClick}
      tabIndex={-1}
      aria-label={shown ? "Hide password" : "Show password"}
      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
      data-testid={testId}
    >
      {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  );

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-password`}>{label}</Label>
        <div className="relative">
          <Input
            id={`${idPrefix}-password`}
            type={showPw ? "text" : "password"}
            required
            autoComplete={autoComplete}
            value={pw}
            onChange={(e) => setPassword(e.target.value)}
            className="pr-10"
            data-testid={`${idPrefix}-password-input`}
          />
          <ToggleBtn shown={showPw} onClick={() => setShowPw((s) => !s)} testId={`${idPrefix}-password-toggle`} />
        </div>
        {strength.level > 0 && (
          <div className="mt-2 flex items-center gap-2" data-testid={`${idPrefix}-strength`} data-level={strength.level}>
            <div className="flex flex-1 gap-1">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className={`h-1.5 flex-1 rounded-full transition-colors ${
                    i <= strength.level ? STRENGTH_BAR[strength.level] : "bg-muted"
                  }`}
                />
              ))}
            </div>
            <span className={`text-xs font-[560] ${STRENGTH_TEXT[strength.level]}`}>{strength.label}</span>
          </div>
        )}
        <ul className="mt-2 space-y-1" data-testid={`${idPrefix}-rules`}>
          {rules.map((r) => (
            <li
              key={r.id}
              className={`flex items-center gap-1.5 text-xs transition-colors ${
                r.ok ? "text-green-600" : "text-muted-foreground"
              }`}
              data-testid={`${idPrefix}-rule-${r.id}`}
              data-ok={r.ok ? "true" : "false"}
            >
              {r.ok ? <Check className="h-3.5 w-3.5 shrink-0" /> : <X className="h-3.5 w-3.5 shrink-0 opacity-50" />}
              {r.label}
            </li>
          ))}
        </ul>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-confirm`}>{confirmLabel}</Label>
        <div className="relative">
          <Input
            id={`${idPrefix}-confirm`}
            type={showConfirm ? "text" : "password"}
            required
            autoComplete={autoComplete}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="pr-10"
            data-testid={`${idPrefix}-confirm-input`}
          />
          <ToggleBtn shown={showConfirm} onClick={() => setShowConfirm((s) => !s)} testId={`${idPrefix}-confirm-toggle`} />
        </div>
        {hasConfirm && (
          <p
            className={`text-xs font-[520] ${match ? "text-green-600" : "text-destructive"}`}
            data-testid={`${idPrefix}-match`}
          >
            {match ? "Passwords match." : "Passwords don't match."}
          </p>
        )}
      </div>
    </div>
  );
}
